import { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import CommonMultiSelect, { SelectOption } from "../common/CommonMultiSelect";
import { useCountries } from "../../hooks/useCountries";
import { useMe } from "../../auth/MeContext";
import { PERM } from "../../auth/permissions";
import api from "../../hooks/api";
import { pathAPI } from "../../utils/pathApi";

interface CategoryCountrySelectProps {
  categoryId: number;
  national: number[] | null;
  active: boolean;
  onUpdated: (id: number, countryIds: number[] | null) => void;
}

const CategoryCountrySelect = ({
  categoryId,
  national,
  active,
  onUpdated,
}: CategoryCountrySelectProps) => {
  const { t } = useTranslation();
  const { can } = useMe();
  const [{ data: countries }] = useCountries();
  const [selected, setSelected] = useState<Array<string | number>>([]);
  const [saving, setSaving] = useState(false);

  const countryOptions: SelectOption[] = useMemo(
    () => [
      { label: "All", value: "all" },
      ...countries.map((country) => ({
        label: country.code
          ? `${country.name} (${country.code})`
          : country.name,
        value: country.id,
      })),
    ],
    [countries]
  );

  const allCountryIds = useMemo(
    () => countries.map((country) => country.id),
    [countries]
  );

  // Seed selection from the row's national list. null/empty/[0] means "All".
  useEffect(() => {
    if (allCountryIds.length === 0) return;
    const isAll = !national || national.length === 0 || national.includes(0);
    setSelected(isAll ? ["all", ...allCountryIds] : national);
  }, [national, allCountryIds]);

  const persist = async (next: Array<string | number>) => {
    const previous = selected;
    const ids = next.filter((value): value is number => typeof value === "number");
    const isAll = next.includes("all");
    const payload = isAll ? [0] : ids;
    setSaving(true);
    try {
      await api.PUT(
        `${pathAPI.categoryDetail}/${categoryId}/countries`,
        { countryIds: payload },
        {},
        true
      );
      onUpdated(categoryId, isAll ? null : ids);
    } catch {
      setSelected(previous);
      toast.error(t("category.messages.error"));
    } finally {
      setSaving(false);
    }
  };

  // Keep "All" and the individual items in sync (same rule as AddModal).
  const handleChange = (values: Array<string | number>) => {
    const hadAll = selected.includes("all");
    const hasAll = values.includes("all");
    const items = values.filter((value) => value !== "all");

    let next: Array<string | number>;
    if (hasAll && !hadAll) {
      next = ["all", ...allCountryIds];
    } else if (!hasAll && hadAll && items.length === allCountryIds.length) {
      next = [];
    } else if (
      allCountryIds.length > 0 &&
      items.length === allCountryIds.length
    ) {
      next = ["all", ...items];
    } else {
      next = items;
    }

    setSelected(next);
    void persist(next);
  };

  if (!active) {
    return (
      <Box component="span" sx={{ color: "#b3b7bd", fontSize: 16 }}>
        —
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 210 }}>
      <CommonMultiSelect
        options={countryOptions}
        value={selected}
        onChange={handleChange}
        placeholder={t("category.fields.national")}
        disabled={saving || !can(PERM.CATEGORY_UPDATE)}
        size="small"
      />
    </Box>
  );
};

export default CategoryCountrySelect;
